import { useEffect, useState } from "react";
import { NewCard } from "@components/Cards/Templates/newCard";

import {
  KeycloakGroupMappings,
  keycloakSubGroupMappings,
} from "@constants/KeycloakSettings";
import { ChevronDownIcon, ChevronUpIcon } from "@heroicons/react/16/solid";
import { error_message } from "../../../../constants/Errors";

type Group = { id: string; name: string; parent?: string };

const GROUP_LABELS: Record<string, string> = {
  global_groups: "グローバル",
  settings_groups: "設定",
  data_groups: "データ",
  kakely_groups: "KAKELY",
  yobely_groups: "YOBELY",
};

const PARENT_TO_KEY: Record<string, keyof typeof GROUP_LABELS> = {
  global: "global_groups",
  settings: "settings_groups",
  data: "data_groups",
  kakely: "kakely_groups",
  yobely: "yobely_groups",
};

export const PermissionsCard = ({
  keycloakGroups,
  tenantData,
  loading,
  handleCheckboxChange,
}: {
  keycloakGroups: any;
  tenantData: any;
  updateTenantData: (data: any) => void;
  loading: boolean;
  handleCheckboxChange: (group: Group) => void;
}) => {
  const groupedEntries = Object.entries(keycloakGroups ?? {})
    .filter(
      ([key, arr]) =>
        key.endsWith("_groups") && Array.isArray(arr) && arr.length > 0,
    )
    .sort(([a], [b]) => {
      const ai = Object.keys(GROUP_LABELS).indexOf(a);
      const bi = Object.keys(GROUP_LABELS).indexOf(b);
      return (ai === -1 ? 999 : ai) - (bi === -1 ? 999 : bi);
    });

  const [expandedItems, setExpandedItems] = useState<Record<string, boolean>>(
    {},
  );

  const toggleItem = (key: string) => {
    setExpandedItems((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const labelForParent = (parent?: string) => {
    if (!parent) return undefined;
    const key = PARENT_TO_KEY[parent.toLowerCase()];
    return key ? GROUP_LABELS[key] : undefined;
  };

  const hasAnyGroups = groupedEntries.length > 0;

  const userKeycloakGroups: Group[] =
    (tenantData?.keycloak_groups?.tenant_groups as Group[]) ?? [];

  const isChecked = (groupId: string) =>
    Array.isArray(userKeycloakGroups) &&
    userKeycloakGroups.some((g) => g.id === groupId);

  const checkSubGroups = (groups: Group[], parentLabel: string) => {
    const subgroups: Record<string, Group[]> = {};

    groups.forEach((group) => {
      const parent = group.parent ?? "";
      const mappedTopLevel = labelForParent(parent);
      if (!mappedTopLevel) {
        if (parent && parent.toUpperCase() !== parentLabel.toUpperCase()) {
          if (!subgroups[parent]) subgroups[parent] = [];
          subgroups[parent].push(group);
        }
      }
    });

    return subgroups;
  };

  const dropSubGroups = (
    subgroups: Record<string, Group[]>,
    groups: Group[],
  ) => {
    const subGroupLabels = Object.keys(subgroups);
    return groups.filter(
      (group) =>
        !subGroupLabels.includes(group.parent ?? "") &&
        !subGroupLabels.includes(group.name),
    );
  };

  return (
    <NewCard
      header="権限グループ設定"
      bodyLabel=""
      loading={loading}
      bodyContents={
        <>
          {!hasAnyGroups ? (
            <div className="text-xl">
              {error_message.tenant.no_groups_found}
            </div>
          ) : (
            <div className="flex flex-col gap-8 min-h-[4rem]">
              {groupedEntries.map(([key, groups]) => {
                const rawLabel = key.replace(/_groups$/, "");
                const label = GROUP_LABELS[key] ?? rawLabel;
                const sorted = [...groups].sort((a, b) =>
                  a.name.localeCompare(b.name),
                );

                const seen = new Set<string>();
                const unique = sorted.filter((g) =>
                  seen.has(g.id) ? false : (seen.add(g.id), true),
                );

                const subgroups = checkSubGroups(unique, label);
                const topLevelGroups = dropSubGroups(subgroups, unique);

                return (
                  <section key={label}>
                    <h4
                      className="flex items-center gap-2 text-lg font-semibold text-gray-700 cursor-pointer select-none"
                      onClick={() => toggleItem(key)}
                    >
                      {KeycloakGroupMappings[label] || label}
                      {expandedItems[key] ? (
                        <ChevronUpIcon className="w-6 h-6" />
                      ) : (
                        <ChevronDownIcon className="w-6 h-6" />
                      )}
                    </h4>

                    <div className="flex flex-wrap gap-4">
                      {expandedItems[key] &&
                        topLevelGroups.map((group) => (
                          <label
                            key={group.id}
                            className="flex items-center gap-2 text-lg font-semibold text-gray-700 cursor-pointer select-none"
                          >
                            <span className="text-base">
                              {KeycloakGroupMappings[group.name] || group.name}
                            </span>
                          </label>
                        ))}
                    </div>

                    {expandedItems[key] &&
                      Object.keys(subgroups).length > 0 && (
                        <div className="ml-4 mt-2 text-black gap-5">
                          {Object.keys(subgroups).map((parent) => (
                            <div key={parent}>
                              <h5
                                className="mt-2 font-bold text-md flex items-center gap-2 cursor-pointer select-none"
                                onClick={() => toggleItem(parent)}
                              >
                                {" "}
                                {keycloakSubGroupMappings[parent] ||
                                  "未定義のサブグループ"}
                                {expandedItems[parent] ? (
                                  <ChevronUpIcon className="w-6 h-6" />
                                ) : (
                                  <ChevronDownIcon className="w-6 h-6" />
                                )}
                              </h5>

                              <div className="flex mt-2 flex-wrap gap-5">
                                {expandedItems[parent] &&
                                  subgroups[parent].map((sub) => (
                                    <label
                                      key={sub.id}
                                      className="flex items-center gap-2"
                                    >
                                      <input
                                        type="checkbox"
                                        className="w-4 h-4"
                                        checked={isChecked(sub.id)}
                                        onChange={() =>
                                          handleCheckboxChange(sub)
                                        }
                                      />
                                      <span className="text-base">
                                        {KeycloakGroupMappings[sub.name] ||
                                          "未定義の機能"}
                                      </span>
                                    </label>
                                  ))}
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                  </section>
                );
              })}
            </div>
          )}

          {/* <div className="mt-5 divider" /> */}
        </>
      }
    />
  );
};
